import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaVolumeDown,
  FaExpand,
  FaCompress,
  FaCog
} from 'react-icons/fa'
import './VideoPlayer.scss'

const VideoPlayer = ({
  src,
  poster,
  autoPlay = false,
  width = '100%',
  height = '100%',
  style,
  className,
  onEnded,
  onReady,
  showVolume = true,
  controlledVolume // New prop for external volume control
}) => {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const progressBarRef = useRef(null)
  const controlsTimeoutRef = useRef(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(
    controlledVolume !== undefined ? controlledVolume : 1
  )
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [bufferedProgress, setBufferedProgress] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)

  // Enhanced scrubbing state
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [wasPlayingBeforeScrub, setWasPlayingBeforeScrub] = useState(false)
  const [scrubbingTime, setScrubbingTime] = useState(null)

  // Network stats
  const [loadedBytes, setLoadedBytes] = useState(0)
  const [totalBytes, setTotalBytes] = useState(0)
  const [downloadSpeed, setDownloadSpeed] = useState(0)
  const lastLoadedRef = useRef(0)
  const lastLoadTimeRef = useRef(Date.now())

  // Settings menu
  const [showSettings, setShowSettings] = useState(false)
  const [showStatsInSettings, setShowStatsInSettings] = useState(false)

  // Initialize video
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    setIsLoading(true)

    // Fetch actual file size from AWS URL
    if (src) {
      fetch(src, { method: 'HEAD' })
        .then(response => {
          const contentLength = response.headers.get('Content-Length')
          if (contentLength) {
            setTotalBytes(parseInt(contentLength, 10))
          }
        })
        .catch(err => {
          console.warn('Could not fetch video size:', err)
        })
    }

    if (autoPlay) {
      video.play().catch(() => {
        setIsMuted(true)
        video.muted = true
        video.play().catch(e => console.error('Autoplay failed:', e))
      })
    }
  }, [autoPlay, src])

  useEffect(() => {
    if (controlledVolume !== undefined && videoRef.current) {
      const newVol = Math.max(0, Math.min(1, controlledVolume))
      setVolume(newVol)
      videoRef.current.volume = newVol
    }
  }, [controlledVolume])

  // Handle Fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement)
      )
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
    }
  }, [])

  // Global listeners for scrubbing
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isScrubbing) {
        handleScrubEnd()
      }
    }

    const handleGlobalMouseMove = e => {
      if (isScrubbing) {
        handleScrubMove(e)
      }
    }

    if (isScrubbing) {
      document.addEventListener('mouseup', handleGlobalMouseUp)
      document.addEventListener('touchend', handleGlobalMouseUp)
      document.addEventListener('mousemove', handleGlobalMouseMove)
      document.addEventListener('touchmove', handleGlobalMouseMove)
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp)
      document.removeEventListener('touchend', handleGlobalMouseUp)
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('touchmove', handleGlobalMouseMove)
    }
  }, [isScrubbing])

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play().catch(e => console.error('Play failed:', e))
    } else {
      video.pause()
    }
  }, [])

  const handleTimeUpdate = () => {
    const video = videoRef.current
    if (!video || isScrubbing) return

    const current = video.currentTime
    const dur = video.duration || 0
    setCurrentTime(current)
    if (dur > 0) {
      setProgress((current / dur) * 100)
    }
  }

  const handleLoadedMetadata = () => {
    const video = videoRef.current
    if (!video) return
    setDuration(video.duration)
    if (onReady) onReady()
  }

  const handleCanPlay = () => {
    setIsLoading(false)
  }

  const handleProgress = () => {
    const video = videoRef.current
    if (!video || !video.duration) return

    // Calculate maximum point reached in buffering
    let maxBufferedEnd = 0
    for (let i = 0; i < video.buffered.length; i++) {
      if (video.buffered.end(i) > maxBufferedEnd) {
        maxBufferedEnd = video.buffered.end(i)
      }
    }

    if (maxBufferedEnd > 0) {
      const percent = Math.min(100, (maxBufferedEnd / video.duration) * 100)
      setBufferedProgress(percent)

      if (totalBytes > 0) {
        const currentLoadedBytes = (percent / 100) * totalBytes

        // Speed calculation
        const now = Date.now()
        const timeDiff = (now - lastLoadTimeRef.current) / 1000
        const bytesDiff = currentLoadedBytes - lastLoadedRef.current

        if (timeDiff >= 1 && bytesDiff > 0) {
          const speedKbps = (bytesDiff * 8) / 1024 / timeDiff
          setDownloadSpeed(prev =>
            prev === 0 ? speedKbps : prev * 0.7 + speedKbps * 0.3
          )

          lastLoadedRef.current = currentLoadedBytes
          lastLoadTimeRef.current = now
        }

        setLoadedBytes(currentLoadedBytes)
      }
    }
  }

  const handleWaiting = () => {
    setIsLoading(true)
  }

  const handlePlaying = () => {
    // Only hide loading if we're actually playing
    const video = videoRef.current
    if (video && !video.paused && video.readyState >= 3) {
      setIsLoading(false)
    }
  }

  const handleSeeking = () => {
    const video = videoRef.current
    if (!video) return

    // Always show loading when seeking
    setIsLoading(true)
  }

  const handleSeeked = () => {
    const video = videoRef.current
    if (!video) return

    // Check if the seeked position is buffered
    const currentTime = video.currentTime
    let isBuffered = false

    for (let i = 0; i < video.buffered.length; i++) {
      if (
        currentTime >= video.buffered.start(i) &&
        currentTime <= video.buffered.end(i)
      ) {
        isBuffered = true
        break
      }
    }

    // Only hide loading if position is buffered and ready to play
    if (isBuffered && video.readyState >= 3) {
      setIsLoading(false)
    } else {
      // Keep loading visible if seeking to unbuffered region
      setIsLoading(true)
    }
  }

  // --- Enhanced Scrubbing Logic ---
  const handleScrubStart = e => {
    e.stopPropagation()
    e.preventDefault()

    const video = videoRef.current
    if (!video || !video.duration) return

    setIsScrubbing(true)
    setWasPlayingBeforeScrub(!video.paused)

    // Pause immediately
    if (!video.paused) {
      video.pause()
    }

    // Perform initial seek
    const newTime = calculateSeekTime(e)
    if (newTime !== null) {
      seekToTime(newTime)
    }
  }

  const handleScrubMove = e => {
    if (!isScrubbing) return

    const newTime = calculateSeekTime(e)
    if (newTime !== null) {
      seekToTime(newTime)
    }
  }

  const calculateSeekTime = e => {
    const progressBar = progressBarRef.current
    const video = videoRef.current

    if (!progressBar || !video || !video.duration) return null

    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX)
    if (clientX === undefined) return null

    const rect = progressBar.getBoundingClientRect()
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
    const percentage = x / rect.width

    return percentage * video.duration
  }

  const seekToTime = time => {
    const video = videoRef.current
    if (!video) return

    setScrubbingTime(time)

    // Update UI immediately
    const percentage = (time / video.duration) * 100
    setProgress(percentage)
    setCurrentTime(time)

    // Perform actual seek
    if (video.seekable.length > 0) {
      const seekableStart = video.seekable.start(0)
      const seekableEnd = video.seekable.end(video.seekable.length - 1)
      const clampedTime = Math.max(seekableStart, Math.min(seekableEnd, time))
      video.currentTime = clampedTime
    } else {
      video.currentTime = time
    }
  }

  const handleScrubEnd = () => {
    if (!isScrubbing) return

    const video = videoRef.current
    if (!video) return

    setIsScrubbing(false)
    setScrubbingTime(null)

    // Resume playback if it was playing before
    if (wasPlayingBeforeScrub) {
      video.play().catch(e => console.error('Resume failed:', e))
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return

    video.muted = !video.muted
    setIsMuted(!isMuted)
  }

  const handleVolumeChange = e => {
    const video = videoRef.current
    if (!video) return

    const newVolume = parseFloat(e.target.value)
    video.volume = newVolume
    setVolume(newVolume)

    if (newVolume === 0) {
      setIsMuted(true)
      video.muted = true
    } else if (isMuted) {
      setIsMuted(false)
      video.muted = false
    }
  }

  const toggleSettings = () => {
    setShowSettings(!showSettings)
  }

  const toggleFullscreen = () => {
    const container = containerRef.current
    if (!container) return

    const isFullscreenVal =
      document.fullscreenElement || document.webkitFullscreenElement

    if (!isFullscreenVal) {
      if (container.requestFullscreen) {
        container.requestFullscreen().catch(err => {
          console.error('Error attempting to enable fullscreen:', err)
        })
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }
  }

  const changePlaybackSpeed = () => {
    const video = videoRef.current
    if (!video) return

    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2]
    const currentIndex = speeds.indexOf(playbackSpeed)
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length]

    video.playbackRate = nextSpeed
    setPlaybackSpeed(nextSpeed)
  }

  const formatTime = time => {
    if (!time || isNaN(time)) return '00:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const formatBytes = bytes => {
    if (bytes === 0) return '0 MB'
    const mb = bytes / (1024 * 1024)
    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`
    }
    const kb = bytes / 1024
    return `${kb.toFixed(0)} KB`
  }

  const formatSpeed = kbps => {
    if (kbps === 0) return '0 Kbps'
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(1)} Mbps`
    }
    return `${kbps.toFixed(0)} Kbps`
  }

  const handleMouseEnter = () => {
    setIsHovering(true)
    setShowControls(true)

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
  }

  const handleMouseLeave = () => {
    setIsHovering(false)

    if (isPlaying && !isScrubbing) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
      }, 2000)
    }
  }

  const handleMouseMove = () => {
    if (!showControls) {
      setShowControls(true)
    }

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }

    if (isPlaying && !isScrubbing) {
      controlsTimeoutRef.current = setTimeout(() => {
        if (!isHovering) {
          setShowControls(false)
        }
      }, 3000)
    }
  }

  const handlePause = () => {
    setIsPlaying(false)
    setShowControls(true)
  }

  const handlePlay = () => {
    setIsPlaying(true)
  }

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [])

  // Close settings when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        showSettings &&
        !e.target.closest('.shl-settings-menu') &&
        !e.target.closest('.shl-control-btn')
      ) {
        setShowSettings(false)
      }
    }

    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSettings])

  // Reset download speed when not actively downloading
  useEffect(() => {
    const checkDownloadActivity = setInterval(() => {
      const now = Date.now()
      const timeSinceLastUpdate = (now - lastLoadTimeRef.current) / 1000

      // If no progress for 2 seconds, reset speed
      if (timeSinceLastUpdate > 2) {
        setDownloadSpeed(0)
      }
    }, 1000)

    return () => clearInterval(checkDownloadActivity)
  }, [])

  // Trigger progress check more frequently
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const progressInterval = setInterval(() => {
      handleProgress()
    }, 500) // Check every 500ms for smoother speed updates

    return () => clearInterval(progressInterval)
  }, [totalBytes])

  return (
    <div
      ref={containerRef}
      className={classNames('shl-video-player', className, {
        'is-fullscreen': isFullscreen,
        'hide-cursor': isPlaying && !showControls && !isScrubbing
      })}
      style={{ width, height, ...style }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onContextMenu={e => e.preventDefault()}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="shl-video-element"
        onClick={togglePlay}
        controls={false}
        controlsList="nodownload"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onProgress={handleProgress}
        onSeeking={handleSeeking}
        onSeeked={handleSeeked}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={onEnded}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        playsInline
        preload="auto"
      />

      {/* Play/Pause Overlay (Center) */}
      {!isPlaying && !isLoading && (
        <div className="shl-video-overlay-center" onClick={togglePlay}>
          <div className="shl-play-button-large">
            <FaPlay className="shl-icon-play" />
          </div>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="shl-video-loader">
          <div className="shl-spinner"></div>
        </div>
      )}

      {/* Control Bar */}
      <div
        className={classNames('shl-video-controls', {
          'is-visible': showControls || !isPlaying || isScrubbing
        })}
      >
        {/* Progress Bar */}
        <div
          className="shl-progress-container"
          ref={progressBarRef}
          onMouseDown={handleScrubStart}
          onTouchStart={handleScrubStart}
        >
          <div className="shl-progress-track">
            <div
              className="shl-progress-buffer"
              style={{ width: `${bufferedProgress}%` }}
            />
            <div
              className="shl-progress-fill"
              style={{ width: `${progress}%` }}
            />
            <div
              className="shl-progress-thumb"
              style={{ left: `${progress}%` }}
            />
          </div>

          {/* Scrubbing time tooltip */}
          {isScrubbing && scrubbingTime !== null && (
            <div
              className="shl-scrub-tooltip"
              style={{ left: `${(scrubbingTime / duration) * 100}%` }}
            >
              {formatTime(scrubbingTime)}
            </div>
          )}
        </div>

        <div className="shl-controls-row">
          <div className="shl-controls-left">
            <button
              className="shl-control-btn"
              onClick={togglePlay}
              disabled={isLoading && !isScrubbing}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <div className="shl-time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div className="shl-controls-right">
            {/* Playback Speed */}
            <button
              className="shl-control-btn shl-speed-btn"
              onClick={changePlaybackSpeed}
              title="Playback speed"
            >
              <span className="shl-speed-text">{playbackSpeed}x</span>
            </button>

            {/* Volume Control with Slider */}
            {showVolume && (
              <div className="shl-volume-control">
                <button className="shl-control-btn" onClick={toggleMute}>
                  {isMuted || volume === 0 ? (
                    <FaVolumeMute />
                  ) : volume < 0.5 ? (
                    <FaVolumeDown />
                  ) : (
                    <FaVolumeUp />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="shl-volume-slider"
                />
              </div>
            )}

            {/* Settings Button */}
            <button
              className={classNames('shl-control-btn', {
                'is-active': showSettings
              })}
              onClick={toggleSettings}
              title="Settings"
            >
              <FaCog />
            </button>

            <button className="shl-control-btn" onClick={toggleFullscreen}>
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
          </div>
        </div>

        {/* Settings Menu */}
        {showSettings && (
          <div className="shl-settings-menu">
            <div className="shl-settings-header">
              <span>Settings</span>
            </div>

            <div className="shl-settings-item">
              <label className="shl-settings-label">
                <input
                  type="checkbox"
                  checked={showStatsInSettings}
                  onChange={e => setShowStatsInSettings(e.target.checked)}
                  className="shl-settings-checkbox"
                />
                <span>Show Network Stats</span>
              </label>
            </div>

            {showStatsInSettings && (
              <div className="shl-settings-stats">
                <div className="shl-stats-row">
                  <span className="shl-stats-label">Download Speed:</span>
                  <span className="shl-stats-value">
                    {isLoading ? (
                      <div className="shl-mini-spinner-inline"></div>
                    ) : downloadSpeed > 0 ? (
                      formatSpeed(downloadSpeed)
                    ) : loadedBytes >= totalBytes && totalBytes > 0 ? (
                      'Complete'
                    ) : (
                      'Idle'
                    )}
                  </span>
                </div>
                <div className="shl-stats-row">
                  <span className="shl-stats-label">Loaded / Total:</span>
                  <span className="shl-stats-value">
                    {totalBytes > 0
                      ? `${formatBytes(loadedBytes)} / ${formatBytes(
                          totalBytes
                        )}`
                      : 'Calculating...'}
                  </span>
                </div>
                <div className="shl-stats-row">
                  <span className="shl-stats-label">Buffer:</span>
                  <span className="shl-stats-value">
                    {bufferedProgress.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  autoPlay: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  className: PropTypes.string,
  onEnded: PropTypes.func,
  onReady: PropTypes.func
}

export default VideoPlayer
